/**
 * Générateur — Attendu 960 — « Définir et tracer les médianes dans un triangle. »
 * (libellé officiel, BO)
 *
 * Niveau : 5ème | Domaine : Espace et géométrie / Triangles
 * Mise en conformité avec la spécification `Attendu-5e/specifications-tanoed/5e/960.json`
 * (campagne 5e, 2026-09-21). La spécification est la seule référence.
 *
 * ── Plan de cartes (version finale) ──────────────────────────────────────────
 *
 *  Définition de référence, écrite partout sous la même forme : « La médiane
 *  issue d'un sommet d'un triangle est la DROITE qui passe par ce sommet et par
 *  le MILIEU du côté opposé. » (`definition_de_reference`.)
 *
 *  1. def         — niveau 1 : compléter la définition (menus déroulants) et la
 *                   choisir parmi celles des quatre droites remarquables (QCM).
 *  2. vocab       — réglage transverse : « médiane issue du sommet » et
 *                   « médiane relative au côté » désignent la même droite.
 *  3. discriminer — niveau 2 : reconnaître la droite tracée sur une figure
 *                   CODÉE, en concurrence avec la bissectrice (même sommet) et
 *                   la médiatrice (même milieu).
 *  4. tracer      — niveau 3 : construire le milieu, le coder, tracer la
 *                   médiane — trois critères notés séparément.
 *  5. isocele     — niveau 4 : triangle isocèle, la médiane issue du sommet
 *                   principal (construction) et ce qu'elle a de particulier (QCM).
 *  6. concours    — niveau 5 : les trois médianes et leur point de concours,
 *                   jamais nommé (exclusion 7).
 *  7. aires       — niveau 6 : la médiane partage le triangle en deux triangles
 *                   de MÊME AIRE, et il faut le justifier.
 *  Niveau 7 (mélange) : `generate()` sans clé.
 *
 * ── Spécification ────────────────────────────────────────────────────────────
 *
 *  Bornes appliquées :
 *   - côté de référence : entier de 6 à 10 unités de figure ;
 *   - orientation : entier multiple de 10 dans [-170 ; 180], et jamais une
 *     orientation qui rendrait un côté horizontal ou vertical (exclusion 3,
 *     contrôlée à 4 degrés près) ;
 *   - troisième sommet : catalogue fermé, filtré à la construction du module ;
 *   - angle minimal du triangle : 25 degrés (exclusion 1), ce qui écarte du même
 *     coup les triangles aplatis de la tâche de comparaison d'aires
 *     (exclusion 6, vigilance 7) ;
 *   - proportion de triangles particuliers : 2 des 6 formats à figure ;
 *   - nombre de médianes d'un triangle : 3, dit explicitement ;
 *   - critères notés pour un tracé : 3 (milieu construit, milieu codé, médiane
 *     tracée) — le milieu compte donc double, comme la spec le demande ;
 *   - médianes exigées pour le point de concours : 3, jamais 2.
 *  Exclusions : aucun angle sous 25 degrés ; aucune médiane extérieure (elles
 *   rencontrent toujours le côté opposé, c'est une propriété, pas un réglage) ;
 *   aucun côté horizontal ni vertical ; aucune validation par coefficient
 *   directeur (le scoring compare des positions de points) ; aucun triangle
 *   aplati dans la comparaison d'aires ; le mot « centre de gravité »
 *   n'apparaît nulle part.
 *
 *  Erreurs visées → mots-clés (codes des distracteurs, `_meta.params`) :
 *   1. confondre médiane et médiatrice (toutes deux par le milieu d'un côté)
 *      → `mediane_pour_mediatrice` (def_qcm_unique, discriminer_qcm_unique)
 *   2. confondre médiane et bissectrice (toutes deux par un sommet)
 *      → `mediane_pour_bissectrice` (def_qcm_unique, discriminer_qcm_unique)
 *   3. tracer une droite qui passe à peu près par le milieu
 *      → `milieu_a_vue` (`erreursVisees` des constructions ; le milieu est une
 *        tâche notée à part)
 *   4. construire le milieu sans le coder
 *      → `milieu_non_code` (`erreursVisees` de tracer et isocele ; le codage du
 *        milieu est une tâche notée à part)
 *   5. croire que deux droites qui se croisent définissent un point sans qu'il
 *      soit besoin de le poser
 *      → `point_non_pose` (`erreurVisee` de concours ; le point est une tâche)
 *   6. dans un triangle isocèle, croire qu'il n'y a qu'une médiane
 *      → `une_seule_mediane_isocele` (isocele_qcm_unique)
 *   7. chercher deux triangles superposables là où l'on demande deux triangles
 *      de même aire → `superposables_pour_meme_aire` (aires_qcm_unique)
 *   8. croire que le milieu d'un côté est le point situé « au milieu » de la
 *      figure → `milieu_de_la_figure` (def_qcm_unique, aires_qcm_unique)
 *   Un neuvième code, `quatrieme_droite_remarquable`, nomme la proposition
 *   « hauteur » de la tâche de reconnaissance : elle ne sert aucune erreur
 *   visée nommée, mais `objets_geometriques` 4 impose que les QUATRE droites
 *   remarquables soient proposées.
 *
 *  Graduation → cartes : 1 def · 2 discriminer · 3 tracer · 4 isocele ·
 *   5 concours · 6 aires · 7 `generate()` sans clé ; réglage transverse des deux
 *   désignations : carte vocab, et alternance dans les énoncés des tracés.
 *
 *  Non tranché (appliqué tel qu'écrit, signalé dans le rapport) :
 *   - Le codage du milieu par des marques d'égalité n'existe pas comme
 *     primitive du composant de construction : il est demandé en repassant la
 *     moitié [BM] du côté, ce qui reste un critère noté à part.
 *   - `tolerance_de_trace_sur_le_passage_par_un_point` = 0,2 cm : le scoring
 *     compare à 12 px fixes, soit 0,4 carreau, annoncé dans les consignes.
 *   - La sortie papier avec grille d'auto-contrôle et le choix du support
 *     relèvent du rendu, pas du générateur.
 */

import {
    randInt, randomChoice, shuffle, pickLettresSains,
    makeQcm, makeTexteTrou, makeProgrammeConstruction, filtrerDistracteurs,
} from '../../../utils';
import {
    creerFigure, cadreur, imgElement, milieu, pied, dist,
    codageDroitInterieur, marqueInterieur,
} from './svgTriangles';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 960;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });
const meta = (payload, key, params) => {
    payload._meta.generatorKey = key;
    payload._meta.params = params;
    return payload;
};

const L_DEF = 'La définition d\'une médiane';
const L_VOCAB = '« Issue de » ou « relative à »';
const L_DISCRIMINER = 'Reconnaître la droite remarquable';
const L_TRACER = 'Tracer une médiane';
const L_ISO = 'Les médianes d\'un triangle isocèle';
const L_CONCOURS = 'Les trois médianes et leur point de concours';
const L_AIRES = 'Deux triangles de même aire';

const COTE_MIN = 6;
const COTE_MAX = 10;
const ANGLE_MIN = 25;

const PX = 30;
const TOL = 12;
const TOL_TXT = '0,4 carreau';
const PAD = 56;
const VB_FIG = { w: 380, h: 300 };
const COL_B = '#2563EB';
const COL_V = '#1D9E75';
const OR = '#b45309';
const ENCRE = '#334155';

const toRad = (d) => (d * Math.PI) / 180;
const POOL_LETTRES = 'ABCDEFGHJKLMNPRSTUVWXYZ'.split('');
const pickLetters = (n) => pickLettresSains(POOL_LETTRES, n);
const salt = (...n) => n.map((x) => Math.round(x)).join('_');

const E_MEDIATRICE = 'mediane_pour_mediatrice';
const E_BISSECTRICE = 'mediane_pour_bissectrice';
const E_MILIEU_VUE = 'milieu_a_vue';
const E_MILIEU_NON_CODE = 'milieu_non_code';
const E_POINT_NON_POSE = 'point_non_pose';
const E_UNE_ISO = 'une_seule_mediane_isocele';
const E_SUPERPOSABLES = 'superposables_pour_meme_aire';
const E_MILIEU_FIGURE = 'milieu_de_la_figure';
const E_AUTRE = 'quatrieme_droite_remarquable';

/** Les deux désignations de la même droite (`deux_designations_de_la_mediane`). */
const designation = (regime, nA, nB, nC) => (regime === 'sommet'
    ? `médiane issue de ${nA}`
    : `médiane relative au côté [${nB}${nC}]`);

// ═══════════════════════════════════════════════════════════
// GÉOMÉTRIE DES FIGURES
// ═══════════════════════════════════════════════════════════

/**
 * Catalogue fermé de positions du troisième sommet, en fractions du côté [BC] :
 * B à l'origine, C à (1 ; 0), A à (fx ; fy). Filtré à la construction du module
 * par `angle_minimal_du_triangle`. Une médiane rencontrant toujours le côté
 * opposé (exclusion 2), les triangles obtusangles sont admis sans réserve.
 */
const CATALOGUE_TRIANGLE = [
    { fx: 0.30, fy: 0.75 }, { fx: 0.35, fy: 0.95 }, { fx: 0.40, fy: 0.70 },
    { fx: 0.45, fy: 0.90 }, { fx: 0.50, fy: 0.65 }, { fx: 0.50, fy: 1.00 },
    { fx: 0.55, fy: 0.80 }, { fx: 0.60, fy: 0.95 }, { fx: 0.65, fy: 0.70 },
    { fx: 0.70, fy: 0.85 }, { fx: 0.80, fy: 0.60 }, { fx: 0.20, fy: 0.60 },
    { fx: -0.25, fy: 0.85 }, { fx: 1.25, fy: 0.85 },
];
const CATALOGUE_ISOCELE = [
    { fx: 0.5, fy: 0.60 }, { fx: 0.5, fy: 0.75 }, { fx: 0.5, fy: 0.90 },
    { fx: 0.5, fy: 1.05 }, { fx: 0.5, fy: 1.20 },
];

const anglesDe = ({ fx, fy }) => {
    const B = { x: 0, y: 0 };
    const C = { x: 1, y: 0 };
    const A = { x: fx, y: fy };
    const a = dist(B, C);
    const b = dist(A, C);
    const c = dist(A, B);
    const ang = (x, y, z) => (Math.acos(Math.max(-1, Math.min(1, (y * y + z * z - x * x) / (2 * y * z)))) * 180) / Math.PI;
    return { A: ang(a, b, c), B: ang(b, a, c), C: ang(c, a, b) };
};

const angleMinOk = (p) => Math.min(...Object.values(anglesDe(p))) >= ANGLE_MIN;

const TRIANGLES = CATALOGUE_TRIANGLE.filter(angleMinOk);
const ISOCELES = CATALOGUE_ISOCELE.filter(angleMinOk);
const ORIENTATIONS = Array.from({ length: 36 }, (_, i) => -170 + 10 * i);

const poser = (positions) => {
    const cote = randInt(COTE_MIN, COTE_MAX);
    const pos = randomChoice(positions);
    const construire = (theta) => {
        const c = Math.cos(toRad(theta));
        const s = Math.sin(toRad(theta));
        const tr = (x, y) => ({ x: (x * c - y * s) * cote * PX, y: -(x * s + y * c) * cote * PX });
        return { B: tr(0, 0), C: tr(1, 0), A: tr(pos.fx, pos.fy), theta };
    };
    const obliques = (t) => [[t.A, t.B], [t.B, t.C], [t.C, t.A]].every(([P, Q]) => {
        const d = (Math.atan2(-(Q.y - P.y), Q.x - P.x) * 180) / Math.PI;
        const m = ((d % 90) + 90) % 90;
        return m > 4 && m < 86;
    });
    for (const theta of shuffle(ORIENTATIONS)) {
        const t = construire(theta);
        if (obliques(t)) return { ...t, cote, pos };
    }
    const t = construire(35);
    return { ...t, cote, pos };
};

// ═══════════════════════════════════════════════════════════
// ASSEMBLAGE D'UN PROGRAMME DE CONSTRUCTION
// ═══════════════════════════════════════════════════════════

const assembler = ({ points, preShapes, tasks, texte, label, key, params }) => {
    const xs = [];
    const ys = [];
    const noter = (p) => { xs.push(p.x); ys.push(p.y); };
    points.forEach(noter);
    tasks.forEach((t) => {
        const s = t.shape;
        if (s.type === 'segment' || s.type === 'line') { noter(s.p1); noter(s.p2); }
        else { xs.push(s.cx - s.r, s.cx + s.r); ys.push(s.cy - s.r, s.cy + s.r); }
    });
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const dx = PAD - minX;
    const dy = PAD - minY;
    const vb = {
        w: Math.round(Math.max(...xs) - minX + 2 * PAD),
        h: Math.round(Math.max(...ys) - minY + 2 * PAD),
    };
    const a2 = (v) => Math.round(v * 100) / 100;
    const mv = (p) => ({ ...p, x: a2(p.x + dx), y: a2(p.y + dy) });
    const mvShape = (s) => ((s.type === 'segment' || s.type === 'line')
        ? { ...s, p1: mv(s.p1), p2: mv(s.p2) }
        : { ...s, cx: a2(s.cx + dx), cy: a2(s.cy + dy) });
    const payload = makeProgrammeConstruction({
        pxPerCm: PX,
        tolerance: TOL,
        viewBox: vb,
        points: points.map(mv),
        preShapes,
        tasks: tasks.map((t) => ({ ...t, shape: mvShape(t.shape) })),
    }, texte, opts(label));
    return meta(payload, key, params);
};

const tachePoint = (id, P, nom, consigne) => ({
    id, consigne, color: COL_V, pointLabel: nom,
    shape: { type: 'circle', cx: P.x, cy: P.y, r: 0 },
});
const tacheSegment = (id, P, Q, consigne, color = COL_V) => ({
    id, kind: 'segment', consigne, color,
    shape: { type: 'segment', p1: { x: P.x, y: P.y }, p2: { x: Q.x, y: Q.y } },
});

const socle = (t, nA, nB, nC) => ({
    points: [{ name: nA, ...t.A }, { name: nB, ...t.B }, { name: nC, ...t.C }],
    preShapes: [
        { type: 'line', from: nA, to: nB },
        { type: 'line', from: nB, to: nC },
        { type: 'line', from: nC, to: nA },
    ],
});

const paramsFig = (t, niveau, famille, extra) => {
    const g = anglesDe(t.pos);
    return {
        niveau,
        famille,
        triangle_particulier: famille === 'isocele',
        longueur_du_cote_de_reference: t.cote,
        orientation_du_triangle: t.theta,
        position_du_troisieme_sommet: [t.pos.fx, t.pos.fy],
        angles_du_triangle: [g.A, g.B, g.C].map((x) => Math.round(x * 10) / 10),
        nombre_de_medianes_d_un_triangle: 3,
        ...extra,
    };
};

/**
 * Les trois tâches d'un tracé de médiane (`nombre_de_criteres_notes_pour_un_trace`
 * = 3) : le milieu construit, le milieu codé, la médiane tracée. Le milieu
 * compte donc double, comme la spec le demande. Chaque tâche est comparée par
 * des POSITIONS de points, jamais par un coefficient directeur (exclusion 5).
 */
const tachesMediane = (t, nA, nB, nC, nM) => {
    const M = milieu(t.B, t.C);
    return [
        tachePoint('milieu', M, nM,
            `Place le milieu ${nM} du côté [${nB}${nC}]. Reporte la demi-longueur à la règle, ne le place pas à vue.`),
        tacheSegment('codage', t.B, M,
            `Repasse en bleu la moitié [${nB}${nM}] du côté : c'est le codage qui dit que ${nM} est le milieu, donc que ${nB}${nM} = ${nM}${nC}.`,
            COL_B),
        tacheSegment('mediane', t.A, M,
            `Trace la médiane issue de ${nA}, de ${nA} jusqu'à ${nM}.`),
    ];
};

const RAPPEL_TOL = `<p><em>Chaque tracé est juste si ses extrémités tombent à moins de ${TOL_TXT} de `
    + 'leur position. Les trois tâches sont comptées séparément.</em></p>';

// ═══════════════════════════════════════════════════════════
// Carte 1 — def (niveau 1)
// ═══════════════════════════════════════════════════════════

const genDefDrag = () => {
    const [nA, nB, nC] = pickLetters(3);
    // Ni « centre de gravité » ni « concourantes » nommées : le concours des
    // médianes n'est pas au programme de ce niveau (exclusion 7).
    const phrase = `La médiane issue du sommet ${nA} du triangle ${nA}${nB}${nC} est la {{droite}} `
        + `qui passe par ${nA} et par le {{milieu}} du côté opposé [${nB}${nC}]. `
        // « et c'est la même droite » ne disait pas la même QUE QUOI (retour prof
        // 2026-09-27). Ce que la phrase énonce, c'est que les deux NOMS désignent un
        // seul objet : on le dit. Pas de « : » dans une phrase à trous, le rendu
        // couperait la ligne au premier deux-points.
        + `On l'appelle aussi la médiane {{relative}} au côté [${nB}${nC}], et ces deux `
        + 'noms désignent la {{même}} droite. Un triangle a {{3}} médianes, une par sommet.';
    const distracteurs = filtrerDistracteurs(phrase, [
        'segment', 'longueur', 'sommet', 'perpendiculaire', 'médiatrice', '2',
    ]);
    const texte = `<p>Complète la définition d'une médiane du triangle ${nA}${nB}${nC}.</p>`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_DEF), mode: 'drag', distracteurs, strict: false,
    });
    return meta(payload, 'def_texte_trou_drag', {
        niveau: 1,
        nombre_de_medianes_d_un_triangle: 3,
        erreursVisees: [E_MEDIATRICE],
    });
};

const genDefQcm = () => {
    const [nA, nB, nC] = pickLetters(3);
    const regime = randomChoice(['sommet', 'cote']);
    const options = [
        { choix: `La droite qui passe par ${nA} et par le milieu de [${nB}${nC}].`, code: null },
        { choix: `La droite perpendiculaire à (${nB}${nC}) qui passe par le milieu de [${nB}${nC}].`, code: E_MEDIATRICE },
        { choix: `La droite qui passe par ${nA} et qui partage l'angle de sommet ${nA} en deux angles égaux.`, code: E_BISSECTRICE },
        { choix: `La droite qui passe par ${nA} et par le point situé au milieu de la figure.`, code: E_MILIEU_FIGURE },
    ];
    const texte = `<p>Dans le triangle ${nA}${nB}${nC}, laquelle de ces droites est la `
        + `<b>${designation(regime, nA, nB, nC)}</b> ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_DEF));
    return meta(payload, 'def_qcm_unique', {
        niveau: 1,
        regime_de_designation: regime,
        nombre_de_medianes_d_un_triangle: 3,
        distracteurs: options.slice(1),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — vocab (réglage transverse des deux désignations)
// ═══════════════════════════════════════════════════════════

const genVocabQcm = () => {
    const [nU, nV, nB] = pickLetters(3);
    const options = [
        { choix: `La médiane issue de ${nU}, car ${nU} est le sommet opposé au côté [${nV}${nB}].`, code: null },
        { choix: `La médiane issue de ${nV}, car ${nV} est une extrémité du côté [${nV}${nB}].`, code: E_MILIEU_FIGURE },
        { choix: `La médiane issue de ${nB}, car ${nB} est une extrémité du côté [${nV}${nB}].`, code: E_MILIEU_FIGURE },
        { choix: `La médiatrice de [${nV}${nB}], car elle passe par le milieu de ce côté.`, code: E_MEDIATRICE },
    ];
    const texte = `<p>Dans le triangle ${nU}${nV}${nB}, quelle droite est la médiane `
        + `<b>relative au côté [${nV}${nB}]</b> ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_VOCAB));
    return meta(payload, 'vocab_qcm_unique', {
        niveau: 1,
        regime_de_designation: 'cote',
        nombre_de_medianes_d_un_triangle: 3,
        distracteurs: options.slice(1),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — discriminer (niveau 2) : figure codée
// ═══════════════════════════════════════════════════════════

const DROITES = [
    { id: 'mediane', nom: 'une médiane' },
    { id: 'mediatrice', nom: 'une médiatrice' },
    { id: 'bissectrice', nom: 'une bissectrice' },
    { id: 'hauteur', nom: 'une hauteur' },
];

/** Le code du distracteur, selon la paire (droite tracée, droite proposée). */
const codePaire = (tracee, proposee) => {
    const paire = [tracee, proposee].sort().join('-');
    if (paire === 'mediane-mediatrice') return E_MEDIATRICE;
    if (paire === 'bissectrice-mediane') return E_BISSECTRICE;
    return E_AUTRE;
};

/** Figure d'une droite remarquable, avec SON codage et rien d'autre. */
const figDroite = (t, quelle, nA, nB, nC, s) => {
    const m0 = milieu(t.B, t.C);
    const d0 = dist(t.B, t.C);
    const u0 = { x: (t.C.x - t.B.x) / d0, y: (t.C.y - t.B.y) / d0 };
    const pts = [t.A, t.B, t.C];
    if (quelle === 'mediatrice') {
        const l = d0 * 0.55;
        pts.push({ x: m0.x - u0.y * l, y: m0.y + u0.x * l }, { x: m0.x + u0.y * l, y: m0.y - u0.x * l });
    }
    const cad = cadreur(pts, VB_FIG, 46);
    const A = cad(t.A);
    const B = cad(t.B);
    const C = cad(t.C);
    const fig = creerFigure(VB_FIG, s);
    fig.segment(A, B);
    fig.segment(B, C);
    fig.segment(C, A);
    const G = { x: (A.x + B.x + C.x) / 3, y: (A.y + B.y + C.y) / 3 };
    const dir = (P) => (Math.atan2(-(P.y - G.y), P.x - G.x) * 180) / Math.PI;
    fig.point(A, nA, dir(A), 14);
    fig.point(B, nB, dir(B), 14);
    fig.point(C, nC, dir(C), 14);
    const M = milieu(B, C);
    const trace = (P, Q) => fig.brut(`<line x1="${P.x.toFixed(1)}" y1="${P.y.toFixed(1)}" x2="${Q.x.toFixed(1)}" y2="${Q.y.toFixed(1)}" stroke="${OR}" stroke-width="2"/>`);
    if (quelle === 'mediane') {
        trace(A, M);
        fig.codageLongueur(B, M, { couleur: ENCRE });
        fig.codageLongueur(M, C, { couleur: ENCRE });
    } else if (quelle === 'mediatrice') {
        const l = dist(B, C) * 0.55;
        const ux = (C.x - B.x) / dist(B, C);
        const uy = (C.y - B.y) / dist(B, C);
        const P1 = { x: M.x - uy * l, y: M.y + ux * l };
        const P2 = { x: M.x + uy * l, y: M.y - ux * l };
        trace(P1, P2);
        codageDroitInterieur(fig, M, P1, C, { couleur: OR });
        fig.codageLongueur(B, M, { couleur: ENCRE });
        fig.codageLongueur(M, C, { couleur: ENCRE });
    } else if (quelle === 'hauteur') {
        const H = pied(A, B, C);
        trace(A, H);
        codageDroitInterieur(fig, H, A, C, { couleur: OR });
    } else {
        const k = dist(A, B) / (dist(A, B) + dist(A, C));
        const D = { x: B.x + k * (C.x - B.x), y: B.y + k * (C.y - B.y) };
        trace(A, D);
        marqueInterieur(fig, A, B, D, '', { couleur: OR, r: 22 });
        marqueInterieur(fig, A, D, C, '', { couleur: OR, r: 28 });
    }
    return fig;
};

/** Les triangles dont le pied de la hauteur issue de A reste sur [BC]. */
const TRIANGLES_ACUTANGLES = TRIANGLES.filter((p) => p.fx > 0.1 && p.fx < 0.9);

const genDiscriminerQcm = () => {
    const quelle = randomChoice(DROITES);
    // La hauteur n'est tracée que si son pied tombe sur le côté : ailleurs, la
    // figure sortirait du cadre sans servir l'objectif (la médiane, elle,
    // rencontre toujours le côté opposé, exclusion 2).
    const t = poser(quelle.id === 'hauteur' ? TRIANGLES_ACUTANGLES : TRIANGLES);
    const [nA, nB, nC] = pickLetters(3);
    const options = DROITES.map((d) => ({
        choix: d.nom,
        code: d.id === quelle.id ? null : codePaire(quelle.id, d.id),
    }));
    const texte = '<p>Une seule droite remarquable est tracée sur cette figure, avec son codage. '
        + 'Quelle est sa nature ? (La réponse se lit dans le codage, pas dans la position de la droite.)</p>';
    const payload = makeQcm(options.map((o) => o.choix), quelle.nom, texte, {
        ...opts(L_DISCRIMINER),
        elements_additionnels: imgElement(figDroite(t, quelle.id, nA, nB, nC, salt(t.cote, t.theta, randInt(0, 999))).render()),
    });
    return meta(payload, 'discriminer_qcm_unique', paramsFig(t, 2, 'quelconque', {
        droite_tracee: quelle.id,
        distracteurs: options.filter((o) => o.choix !== quelle.nom),
    }));
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — tracer (niveau 3)
// ═══════════════════════════════════════════════════════════

const genTracerPC = () => {
    const t = poser(TRIANGLES);
    const [nA, nB, nC, nM] = pickLetters(4);
    const regime = randomChoice(['sommet', 'cote']);
    const { points, preShapes } = socle(t, nA, nB, nC);
    return assembler({
        points,
        preShapes,
        tasks: tachesMediane(t, nA, nB, nC, nM),
        texte: `<p>Trace la <b>${designation(regime, nA, nB, nC)}</b> du triangle ${nA}${nB}${nC}, `
            + `c'est-à-dire la droite qui passe par ${nA} et par le milieu de [${nB}${nC}]. `
            + `Ce milieu se nomme ${nM} et il est à construire, puis à coder.</p>` + RAPPEL_TOL,
        label: L_TRACER,
        key: 'tracer_programme_construction',
        params: paramsFig(t, 3, 'quelconque', {
            regime_de_designation: regime,
            nombre_de_criteres_notes_pour_un_trace: 3,
            invariants: ['milieu_construit', 'milieu_code', 'mediane_tracee'],
            erreursVisees: [E_MILIEU_VUE, E_MILIEU_NON_CODE],
        }),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — isocele (niveau 4)
// ═══════════════════════════════════════════════════════════

const genIsocelePC = () => {
    const t = poser(ISOCELES);
    const [nA, nB, nC, nM] = pickLetters(4);
    const regime = randomChoice(['sommet', 'cote']);
    const { points, preShapes } = socle(t, nA, nB, nC);
    return assembler({
        points,
        preShapes,
        tasks: tachesMediane(t, nA, nB, nC, nM),
        texte: `<p>Le triangle ${nA}${nB}${nC} est isocèle en ${nA}. Trace la `
            + `<b>${designation(regime, nA, nB, nC)}</b> : le milieu ${nM} de [${nB}${nC}] est à `
            + 'construire, puis à coder.</p>' + RAPPEL_TOL,
        label: L_ISO,
        key: 'isocele_programme_construction',
        params: paramsFig(t, 4, 'isocele', {
            regime_de_designation: regime,
            nombre_de_criteres_notes_pour_un_trace: 3,
            invariants: ['milieu_construit', 'milieu_code', 'mediane_tracee'],
            erreursVisees: [E_MILIEU_VUE, E_MILIEU_NON_CODE],
        }),
    });
};

const genIsoceleQcm = () => {
    const t = poser(ISOCELES);
    const [nA, nB, nC] = pickLetters(3);
    const options = [
        {
            choix: `Elle est aussi la hauteur issue de ${nA} et la médiatrice de [${nB}${nC}] : les trois droites sont confondues.`,
            code: null,
        },
        {
            choix: `C'est la seule médiane du triangle : les deux autres sommets n'en ont pas.`,
            code: E_UNE_ISO,
        },
        {
            choix: `Elle est la médiatrice de [${nB}${nC}], et le triangle n'a donc pas de hauteur issue de ${nA}.`,
            code: E_UNE_ISO,
        },
        {
            choix: `Elle passe par le milieu de [${nA}${nB}] et par le milieu de [${nA}${nC}].`,
            code: E_MILIEU_FIGURE,
        },
    ];
    const texte = `<p>Le triangle ${nA}${nB}${nC} est isocèle en ${nA}. Qu'a de particulier la médiane `
        + `issue de ${nA} ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, {
        ...opts(L_ISO),
        elements_additionnels: imgElement(figDroite(t, 'mediane', nA, nB, nC, salt(t.cote, t.theta, randInt(0, 999))).render()),
    });
    return meta(payload, 'isocele_qcm_unique', paramsFig(t, 4, 'isocele', {
        distracteurs: options.slice(1),
    }));
};

// ═══════════════════════════════════════════════════════════
// Carte 6 — concours (niveau 5)
// ═══════════════════════════════════════════════════════════

const genConcoursPC = () => {
    const t = poser(TRIANGLES);
    const [nA, nB, nC, nG] = pickLetters(4);
    const MA = milieu(t.B, t.C);
    const MB = milieu(t.A, t.C);
    const MC = milieu(t.A, t.B);
    const G = { x: (t.A.x + t.B.x + t.C.x) / 3, y: (t.A.y + t.B.y + t.C.y) / 3 };
    const { points, preShapes } = socle(t, nA, nB, nC);
    return assembler({
        points,
        preShapes,
        tasks: [
            tacheSegment('mediane_A', t.A, MA, `Trace la médiane issue de ${nA}, jusqu'au milieu de [${nB}${nC}].`),
            tacheSegment('mediane_B', t.B, MB, `Trace la médiane issue de ${nB}, jusqu'au milieu de [${nA}${nC}].`),
            tacheSegment('mediane_C', t.C, MC, `Trace la médiane issue de ${nC}, jusqu'au milieu de [${nA}${nB}].`),
            tachePoint('concours', G, nG, `Place le point ${nG} où les trois médianes se coupent.`),
        ],
        texte: `<p>Trace les <b>trois</b> médianes du triangle ${nA}${nB}${nC}, une par sommet, puis `
            + `place leur <b>point de concours</b> ${nG} : les trois médianes passent par un même point. `
            + 'Un point qui n\'est pas posé n\'existe pas sur la figure.</p>'
            + `<p><em>Chaque tracé est juste si ses deux extrémités tombent à moins de ${TOL_TXT} de leur `
            + 'position ; le point de concours est compté à part.</em></p>',
        label: L_CONCOURS,
        key: 'concours_programme_construction',
        params: paramsFig(t, 5, 'quelconque', {
            nombre_de_medianes_exigees_pour_le_point_de_concours: 3,
            invariants: ['mediane_issue_de_A', 'mediane_issue_de_B', 'mediane_issue_de_C', 'point_de_concours'],
            erreursVisees: [E_POINT_NON_POSE, E_MILIEU_VUE],
        }),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 7 — aires (niveau 6) : deux triangles de même aire
// ═══════════════════════════════════════════════════════════

/** Figure de la médiane, les deux triangles obtenus teintés de deux couleurs. */
const figAires = (t, nA, nB, nC, nM, s) => {
    const cad = cadreur([t.A, t.B, t.C], VB_FIG, 48);
    const A = cad(t.A);
    const B = cad(t.B);
    const C = cad(t.C);
    const M = milieu(B, C);
    const fig = creerFigure(VB_FIG, s);
    const poly = (pts, couleur) => fig.brut(
        `<polygon points="${pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}" `
        + `fill="${couleur}" fill-opacity="0.18" stroke="none"/>`);
    poly([A, B, M], '#2563EB');
    poly([A, M, C], '#0f9d63');
    fig.segment(A, B);
    fig.segment(B, C);
    fig.segment(C, A);
    fig.brut(`<line x1="${A.x.toFixed(1)}" y1="${A.y.toFixed(1)}" x2="${M.x.toFixed(1)}" y2="${M.y.toFixed(1)}" stroke="${OR}" stroke-width="2"/>`);
    fig.codageLongueur(B, M, { couleur: ENCRE });
    fig.codageLongueur(M, C, { couleur: ENCRE });
    const G = { x: (A.x + B.x + C.x) / 3, y: (A.y + B.y + C.y) / 3 };
    const dir = (P) => (Math.atan2(-(P.y - G.y), P.x - G.x) * 180) / Math.PI;
    fig.point(A, nA, dir(A), 14);
    fig.point(B, nB, dir(B), 14);
    fig.point(C, nC, dir(C), 14);
    fig.point(M, nM, dir(M), 14);
    return fig;
};

const genAiresQcm = () => {
    const t = poser(TRIANGLES);
    const [nA, nB, nC, nM] = pickLetters(4);
    const options = [
        {
            choix: `Parce que leurs bases [${nB}${nM}] et [${nM}${nC}] sont de même longueur et qu'ils ont la même hauteur, celle issue de ${nA}.`,
            code: null,
        },
        {
            choix: 'Parce qu\'ils sont superposables : on passe de l\'un à l\'autre en le retournant.',
            code: E_SUPERPOSABLES,
        },
        {
            choix: 'Parce qu\'ils ont le même périmètre.',
            code: E_SUPERPOSABLES,
        },
        {
            choix: `Parce que ${nM} est au milieu de la figure, ce qui partage toujours une figure en deux parts égales.`,
            code: E_MILIEU_FIGURE,
        },
    ];
    const texte = `<p>Dans le triangle ${nA}${nB}${nC}, ${nM} est le milieu de [${nB}${nC}] et la médiane `
        + `[${nA}${nM}] est tracée. Les triangles ${nA}${nB}${nM} (en bleu) et ${nA}${nM}${nC} (en vert) `
        + 'ont la <b>même aire</b>. Pourquoi ?</p>';
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, {
        ...opts(L_AIRES),
        elements_additionnels: imgElement(figAires(t, nA, nB, nC, nM, salt(t.cote, t.theta, randInt(0, 999))).render()),
    });
    return meta(payload, 'aires_qcm_unique', paramsFig(t, 6, 'quelconque', {
        distracteurs: options.slice(1),
    }));
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    def_texte_trou_drag: genDefDrag,
    def_qcm_unique: genDefQcm,
    vocab_qcm_unique: genVocabQcm,
    discriminer_qcm_unique: genDiscriminerQcm,
    tracer_programme_construction: genTracerPC,
    isocele_programme_construction: genIsocelePC,
    isocele_qcm_unique: genIsoceleQcm,
    concours_programme_construction: genConcoursPC,
    aires_qcm_unique: genAiresQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_drag: 'Menus', programme_construction: 'Construction',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'def', label: L_DEF, formatsList: buildFormats('def') },
    { id: 'vocab', label: L_VOCAB, formatsList: buildFormats('vocab') },
    { id: 'discriminer', label: L_DISCRIMINER, formatsList: buildFormats('discriminer') },
    { id: 'tracer', label: L_TRACER, formatsList: buildFormats('tracer') },
    { id: 'isocele', label: L_ISO, formatsList: buildFormats('isocele') },
    { id: 'concours', label: L_CONCOURS, formatsList: buildFormats('concours') },
    { id: 'aires', label: L_AIRES, formatsList: buildFormats('aires') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    TRIANGLES, ISOCELES, TRIANGLES_ACUTANGLES, ORIENTATIONS,
    anglesDe, poser, codePaire, DROITES,
};
